import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  plantillaCertificadoPorDefecto,
  type ClausulaCertificado,
  type PlantillaCertificado,
} from '@/lib/certificados/plantillasPorDefecto';

export type TipoCertificado = 'seguridad' | 'pci';

export interface PlantillaCertificadoDB {
  id: string;
  empresa_id: string;
  tipo: TipoCertificado;
  nombre: string;
  objeto: string;
  clausulas: ClausulaCertificado[];
  texto_legal: string;
  activa: boolean;
  created_at: string;
  updated_at: string;
}

export function usePlantillasCertificados() {
  const { perfil } = useAuth();
  const { toast } = useToast();
  const [plantillas, setPlantillas] = useState<PlantillaCertificadoDB[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!perfil?.empresa_id) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('certificado_plantillas')
      .select('*')
      .eq('empresa_id', perfil.empresa_id)
      .order('tipo');
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setPlantillas((data ?? []) as PlantillaCertificadoDB[]);
    }
    setLoading(false);
  }, [perfil?.empresa_id, toast]);

  useEffect(() => { load(); }, [load]);

  const getPlantilla = useCallback(
    (tipo: TipoCertificado): PlantillaCertificado => {
      const p = plantillas.find(x => x.tipo === tipo && x.activa);
      if (p) {
        return {
          tipo: p.tipo,
          nombre: p.nombre,
          objeto: p.objeto,
          clausulas: p.clausulas,
          texto_legal: p.texto_legal,
        };
      }
      return plantillaCertificadoPorDefecto(tipo);
    },
    [plantillas],
  );

  const upsertPlantilla = async (tipo: TipoCertificado, patch: Partial<PlantillaCertificado>) => {
    if (!perfil?.empresa_id) throw new Error('Sin empresa');
    const existente = plantillas.find(p => p.tipo === tipo);
    const base = existente
      ? { nombre: existente.nombre, objeto: existente.objeto, clausulas: existente.clausulas, texto_legal: existente.texto_legal }
      : plantillaCertificadoPorDefecto(tipo);
    const payload = {
      empresa_id: perfil.empresa_id,
      tipo,
      nombre: patch.nombre ?? base.nombre,
      objeto: patch.objeto ?? base.objeto,
      clausulas: (patch.clausulas ?? base.clausulas) as any,
      texto_legal: patch.texto_legal ?? base.texto_legal,
      activa: true,
    };
    if (existente) {
      const { error } = await (supabase as any)
        .from('certificado_plantillas')
        .update(payload)
        .eq('id', existente.id);
      if (error) throw error;
    } else {
      const { error } = await (supabase as any).from('certificado_plantillas').insert(payload);
      if (error) throw error;
    }
    await load();
  };

  const restaurarPorDefecto = async (tipo: TipoCertificado) => {
    const def = plantillaCertificadoPorDefecto(tipo);
    await upsertPlantilla(tipo, def);
  };

  return { plantillas, loading, getPlantilla, upsertPlantilla, restaurarPorDefecto, reload: load };
}
